// What the launch actually spent its time on.
//
// "Sometimes instant, sometimes very slow" is not a bug report anybody can act
// on, and the launch path has several places that are fast when warm and slow
// when cold — the service worker starting, IndexedDB opening, the app chunk
// arriving. Guessing which one is hurting on a particular phone is how a whole
// afternoon disappears. These are the five numbers that tell you instead.
//
// Every value is milliseconds since the navigation started, so they read as a
// timeline: each one is "how far into the launch did this happen".

export interface BootTimings {
  /** Service worker cold start: from the browser waking the worker to the first
   *  byte of the response it served. The worker must be fully parsed and
   *  evaluated before it can answer, so this IS the cost of the worker's size.
   *  Null when no worker handled the navigation (a first visit, or dev). */
  serviceWorkerMs: number | null;
  /** First byte of the HTML, whoever served it. */
  ttfbMs: number | null;
  /** The entry chunk began executing — HTML parsed, 1.5 KB fetched and running. */
  entryMs: number | null;
  /** The settings prefetch resolved. The gap from `entryMs` is the IndexedDB
   *  open, which is the most variable step in the whole launch. */
  settingsMs: number | null;
  /** Real content was on screen and the splash started fading. */
  mountedMs: number | null;
}

function markAt(name: string): number | null {
  const entries = performance.getEntriesByName(name, 'mark');
  const last = entries[entries.length - 1];
  return last === undefined ? null : Math.round(last.startTime);
}

export function bootTimings(): BootTimings {
  const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

  // workerStart is 0 when the navigation never went through a service worker.
  const workerStart = navigation?.workerStart ?? 0;
  const serviceWorkerMs =
    navigation !== undefined && workerStart > 0
      ? Math.round(navigation.responseStart - workerStart)
      : null;

  return {
    serviceWorkerMs,
    ttfbMs: navigation === undefined ? null : Math.round(navigation.responseStart),
    entryMs: markAt('app:entry'),
    settingsMs: markAt('app:settings'),
    mountedMs: markAt('app:mounted'),
  };
}
